import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { api, fmtUSD, fmtNum, EXPENSE_CATEGORIES } from "../lib/api";
import { useYear } from "../context/YearContext";
import { BarChart3, TrendingUp, TrendingDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

export default function YearCompare() {
  const { years } = useYear();
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);

  // Default: pick up to 3 most recent years
  useEffect(() => {
    if (years?.length && selected.length === 0) {
      setSelected(years.slice(0, 3));
    }
  }, [years]); // eslint-disable-line

  useEffect(() => {
    if (selected.length === 0) {
      setData(null);
      return;
    }
    api
      .get(`/reports/compare?years=${selected.join(",")}`)
      .then((r) => setData(r.data));
  }, [selected.join(",")]);

  const toggle = (y) => {
    setSelected(selected.includes(y) ? selected.filter((x) => x !== y) : [...selected, y].sort());
  };

  const rows = data?.summaries || [];

  // Build chart data — one row per metric
  const chartData = rows.length
    ? [
        { name: "Revenue", ...Object.fromEntries(rows.map((s) => [String(s.year), s.total_revenue])) },
        { name: "Expenses", ...Object.fromEntries(rows.map((s) => [String(s.year), s.total_expenses])) },
        { name: "Mileage Ded.", ...Object.fromEntries(rows.map((s) => [String(s.year), s.mileage_deduction])) },
        { name: "Depreciation", ...Object.fromEntries(rows.map((s) => [String(s.year), s.depreciation || 0])) },
        { name: "Net Income", ...Object.fromEntries(rows.map((s) => [String(s.year), s.net_income])) },
      ]
    : [];

  const COLORS = ["#1A4135", "#BC4727", "#E5A835", "#7A4B3A", "#8A9884"];

  const catSet = new Set();
  rows.forEach((s) => Object.keys(s.expenses_by_category || {}).forEach((k) => catSet.add(k)));
  const allCats = EXPENSE_CATEGORIES.filter((c) => catSet.has(c.value));

  return (
    <div>
      <TopBar
        title="Year-over-Year"
        subtitle="Compare tax years side by side. Pick any years you want to see."
      />

      <div className="bg-white border border-[#1F1E1B] p-4 mb-6" data-testid="year-picker">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">Years to compare</div>
        <div className="flex flex-wrap gap-2">
          {years.map((y) => (
            <button
              key={y}
              data-testid={`year-toggle-${y}`}
              onClick={() => toggle(y)}
              className={`px-4 py-1.5 text-sm font-bold tabular border ${
                selected.includes(y)
                  ? "bg-[#1F1E1B] text-white border-[#1F1E1B]"
                  : "bg-white text-[#1F1E1B] border-[#1F1E1B] hover:bg-[#F5F4F0]"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-[#66645E] mt-3">
          {selected.length === 0
            ? "Pick at least one year."
            : `Comparing ${selected.length} year${selected.length > 1 ? "s" : ""}.`}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No years selected"
          hint="Pick years above to build the comparison."
          testid="empty-compare"
        />
      ) : (
        <>
          {/* Summary grid */}
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto mb-6" data-testid="compare-summary-table">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  {rows.map((s) => <th key={s.year} className="num">{s.year}</th>)}
                </tr>
              </thead>
              <tbody>
                <MetricRow label="Gross Revenue" rows={rows} keyName="total_revenue" bold />
                <MetricRow label="Total Expenses" rows={rows} keyName="total_expenses" />
                <MetricRow label="Mileage Deduction" rows={rows} keyName="mileage_deduction" />
                <MetricRow label="Depreciation" rows={rows} keyName="depreciation" />
                <tr className="bg-[#F5F4F0]">
                  <td className="font-bold uppercase tracking-wider text-xs">Net Income</td>
                  {rows.map((s) => (
                    <td key={s.year} className={`num font-display text-lg font-black ${s.net_income >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                      {fmtUSD(s.net_income)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white border border-[#1F1E1B] p-6 mb-6" data-testid="compare-chart">
            <div className="mb-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Trend</div>
              <h3 className="font-display text-xl font-bold">Key Metrics Across Years</h3>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E5E2D6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#66645E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#66645E" }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ border: "1px solid #1F1E1B", borderRadius: 0 }} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                {rows.map((s, i) => (
                  <Bar key={s.year} dataKey={String(s.year)} fill={COLORS[i % COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Expense breakdown by category */}
          {allCats.length > 0 && (
            <div className="bg-white border border-[#1F1E1B] overflow-x-auto mb-6" data-testid="compare-categories-table">
              <div className="p-4 border-b border-[#1F1E1B]">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Expense Detail</div>
                <h3 className="font-display text-lg font-bold">By Category</h3>
              </div>
              <table className="ledger-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    {rows.map((s) => <th key={s.year} className="num">{s.year}</th>)}
                    <th className="num">Change ({rows[0]?.year}→{rows[rows.length - 1]?.year})</th>
                  </tr>
                </thead>
                <tbody>
                  {allCats.map((c) => {
                    const first = rows[0]?.expenses_by_category?.[c.value] || 0;
                    const last = rows[rows.length - 1]?.expenses_by_category?.[c.value] || 0;
                    const delta = last - first;
                    return (
                      <tr key={c.value}>
                        <td>{c.label}</td>
                        {rows.map((s) => (
                          <td key={s.year} className="num">{fmtUSD(s.expenses_by_category?.[c.value] || 0)}</td>
                        ))}
                        <td className={`num font-bold ${delta >= 0 ? "text-[#BC4727]" : "text-[#1A4135]"}`}>
                          {delta >= 0 ? <TrendingUp className="inline w-3 h-3" /> : <TrendingDown className="inline w-3 h-3" />}
                          {" "}{fmtUSD(Math.abs(delta))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Activity counts */}
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto" data-testid="compare-counts-table">
            <div className="p-4 border-b border-[#1F1E1B]">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Activity</div>
              <h3 className="font-display text-lg font-bold">Record Counts</h3>
            </div>
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Activity</th>
                  {rows.map((s) => <th key={s.year} className="num">{s.year}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr><td>Fields tracked</td>{rows.map((s) => <td key={s.year} className="num">{fmtNum(s.field_count, 0)}</td>)}</tr>
                <tr><td>Harvest entries</td>{rows.map((s) => <td key={s.year} className="num">{fmtNum(s.yield_count, 0)}</td>)}</tr>
                <tr><td>Expense entries</td>{rows.map((s) => <td key={s.year} className="num">{fmtNum(s.expense_count, 0)}</td>)}</tr>
                <tr><td>Mileage logs</td>{rows.map((s) => <td key={s.year} className="num">{fmtNum(s.mileage_count, 0)}</td>)}</tr>
                <tr><td>Business miles</td>{rows.map((s) => <td key={s.year} className="num">{fmtNum(s.total_miles_business, 1)}</td>)}</tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function MetricRow({ label, rows, keyName, bold }) {
  return (
    <tr>
      <td className={bold ? "font-semibold" : ""}>{label}</td>
      {rows.map((s) => (
        <td key={s.year} className={`num ${bold ? "font-bold" : ""}`}>{fmtUSD(s[keyName] || 0)}</td>
      ))}
    </tr>
  );
}
